import mixpanel from "mixpanel-browser";
import ReactGA from "react-ga4";

class AnalyticsService {
  constructor() {
    this.isInitialized = false;
    this.mixpanelToken =
      process.env.REACT_APP_MIXPANEL_TOKEN ||
      "faee73412ffe435ae338bf18e9605b6d";
  }

  // Initialize analytics services
  init() {
    if (this.isInitialized) return;

    try {
      // Initialize Mixpanel
      if (this.mixpanelToken && this.mixpanelToken !== "YOUR_MIXPANEL_TOKEN") {
        mixpanel.init(this.mixpanelToken, {
          debug: process.env.NODE_ENV === "development",
          track_pageview: false, // We'll handle page views manually
          persistence: "localStorage",
          property_blacklist: [
            "$current_url",
            "$initial_referrer",
            "$referrer",
          ],
          batch_requests: true,
          secure_cookie: true,
        });
        console.log("✅ Mixpanel initialized");
      } else {
        console.warn(
          "⚠️ Mixpanel token not found. Set REACT_APP_MIXPANEL_TOKEN environment variable."
        );
      }

      this.isInitialized = true;
    } catch (error) {
      console.error("❌ Analytics initialization failed:", error);
      this.trackError("analytics_init_failed", error);
    }
  }

  // Track page views
  trackPageView(page, properties = {}) {
    try {
      const pageData = {
        page,
        url: window.location.href,
        pathname: window.location.pathname,
        search: window.location.search,
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent,
        ...properties,
      };

      // Google Analytics
      ReactGA.send({
        hitType: "pageview",
        page: page,
        ...properties,
      });

      // Mixpanel
      if (this.isMixpanelAvailable()) {
        mixpanel.track(page, pageData);
      }

      console.log("📊 Page view tracked:", page);
    } catch (error) {
      console.error("❌ Page view tracking failed:", error);
    }
  }

  // Track user events
  trackEvent(eventName, properties = {}) {
    try {
      const eventData = {
        timestamp: new Date().toISOString(),
        session_id: this.getSessionId(),
        ...properties,
      };

      // Google Analytics
      ReactGA.event({
        action: eventName,
        category: properties.category || "User Interaction",
        label: properties.label,
        value: properties.value,
      });

      // Mixpanel
      if (this.isMixpanelAvailable()) {
        console.log("Track = " + eventName);
        mixpanel.track(eventName, eventData);
      }

      console.log("📊 Event tracked:", eventName, eventData);
    } catch (error) {
      console.error("❌ Event tracking failed:", error);
    }
  }

  // Track user identification
  identifyUser(userId, userProperties = {}) {
    try {
      // Google Analytics
      ReactGA.set({ userId });

      // Mixpanel
      if (this.isMixpanelAvailable()) {
        mixpanel.identify(userId);
        mixpanel.people.set({
          $name: userProperties.name,
          $email: userProperties.email,
          $phone: userProperties.phone,
          $created: userProperties.createdAt || new Date().toISOString(),
          ...userProperties,
        });
      }

      console.log("👤 User identified:", userId);
    } catch (error) {
      console.error("❌ User identification failed:", error);
    }
  }

  peopleSet(userId, userProperties = {}) {
    try {
      // Google Analytics
      ReactGA.set({ userId });

      // Mixpanel
      if (this.isMixpanelAvailable()) {
        mixpanel.people.set({
          $name: userProperties.name,
          $email: userProperties.email,
          $phone: userProperties.phone,
          ...userProperties,
        });
      }

      console.log("👤 User identified:", userId);
    } catch (error) {
      console.error("❌ User identification failed:", error);
    }
  }

  alias(userId) {
    try {
      // Google Analytics
      ReactGA.set({ userId });

      // Mixpanel
      if (this.isMixpanelAvailable()) {
        mixpanel.alias(userId);
      }

      console.log("👤 User identified:", userId);
    } catch (error) {
      console.error("❌ User identification failed:", error);
    }
  }

  // Track errors and exceptions
  trackError(errorName, error, additionalContext = {}) {
    try {
      const errorData = {
        error_name: errorName,
        error_message: error?.message || error,
        error_stack: error?.stack,
        error_type: error?.constructor?.name || typeof error,
        url: window.location.href,
        pathname: window.location.pathname,
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        session_id: this.getSessionId(),
        ...additionalContext,
      };

      // Google Analytics
      ReactGA.event({
        action: "exception",
        category: "Error",
        label: errorName,
        description: error?.message || error,
        fatal: additionalContext.fatal || false,
      });

      // Mixpanel
      if (this.isMixpanelAvailable()) {
        mixpanel.track("Error Occurred", errorData);
      }

      console.error("🚨 Error tracked:", errorName, errorData);
    } catch (trackingError) {
      console.error("❌ Error tracking failed:", trackingError);
    }
  }

  // Track user interactions (clicks, form submissions, etc.)
  trackInteraction(interactionType, element, properties = {}) {
    try {
      const interactionData = {
        interaction_type: interactionType,
        element_type: element?.tagName?.toLowerCase(),
        element_id: element?.id,
        element_class: element?.className,
        element_text: element?.textContent?.slice(0, 100), // Limit text length
        page: window.location.pathname,
        timestamp: new Date().toISOString(),
        session_id: this.getSessionId(),
        ...properties,
      };

      this.trackEvent("User Interaction", interactionData);
    } catch (error) {
      console.error("❌ Interaction tracking failed:", error);
    }
  }

  // Track form submissions
  trackFormSubmit(formName, formData = {}, success = true) {
    try {
      const submissionData = {
        form_name: formName,
        success,
        field_count: Object.keys(formData).length,
        timestamp: new Date().toISOString(),
        session_id: this.getSessionId(),
        ...formData,
      };

      this.trackEvent("Form Submission", submissionData);
    } catch (error) {
      console.error("❌ Form submission tracking failed:", error);
    }
  }

  // Track API calls
  trackAPICall(endpoint, method, status, duration, error = null) {
    try {
      const apiData = {
        endpoint,
        method,
        status,
        duration_ms: duration,
        success: status >= 200 && status < 300,
        timestamp: new Date().toISOString(),
        session_id: this.getSessionId(),
      };

      if (error) {
        apiData.error_message = error.message;
        apiData.error_type = error.constructor.name;
      }

      this.trackEvent("API Call", apiData);
    } catch (trackingError) {
      console.error("❌ API call tracking failed:", trackingError);
    }
  }

  // Track file uploads
  trackFileUpload(fileName, fileSize, fileType, success = true, error = null) {
    try {
      const uploadData = {
        file_name: fileName,
        file_size_bytes: fileSize,
        file_type: fileType,
        success,
        timestamp: new Date().toISOString(),
        session_id: this.getSessionId(),
      };

      if (error) {
        uploadData.error_message = error.message;
      }

      this.trackEvent("File Upload", uploadData);
    } catch (trackingError) {
      console.error("❌ File upload tracking failed:", trackingError);
    }
  }

  // Track authentication events
  trackAuth(authEvent, method, success = true, error = null) {
    try {
      const authData = {
        auth_event: authEvent, // 'login', 'logout', 'signup', 'verification'
        auth_method: method, // 'email', 'google', 'firebase', etc.
        success,
        timestamp: new Date().toISOString(),
        session_id: this.getSessionId(),
      };

      if (error) {
        authData.error_message = error.message;
      }

      this.trackEvent("Authentication", authData);
    } catch (trackingError) {
      console.error("❌ Auth tracking failed:", trackingError);
    }
  }

  // Helper methods
  isMixpanelAvailable() {
    return (
      mixpanel &&
      this.mixpanelToken &&
      this.mixpanelToken !== "YOUR_MIXPANEL_TOKEN"
    );
  }

  getSessionId() {
    let sessionId = sessionStorage.getItem("analytics_session_id");
    if (!sessionId) {
      sessionId =
        "session_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9);
      sessionStorage.setItem("analytics_session_id", sessionId);
    }
    return sessionId;
  }

  // Set user properties
  setUserProperties(properties) {
    try {
      if (this.isMixpanelAvailable()) {
        mixpanel.people.set(properties);
      }
      console.log("👤 User properties set:", properties);
    } catch (error) {
      console.error("❌ Setting user properties failed:", error);
    }
  }

  // Reset user (for logout)
  resetUser() {
    try {
      if (this.isMixpanelAvailable()) {
        mixpanel.reset();
      }
      sessionStorage.removeItem("analytics_session_id");
      console.log("👤 User reset");
    } catch (error) {
      console.error("❌ User reset failed:", error);
    }
  }
}

// Create singleton instance
const analyticsService = new AnalyticsService();

export default analyticsService;
